import { CalendarSurfaceConfig } from 'owa-calendar-surface-core';
import CalendarEvent from 'owa-calendar-types/lib/types/CalendarEvent';
import { ColorScheme } from 'owa-color-utils/lib/ColorScheme';
import renderAsAllDay from 'owa-calendar-types/lib/utils/renderAsAllDay';
import {
    createCalendarDayContent,
    createMonthDayCellsDecorator,
    getCalendarMonthContent,
    monthItemTemplate,
    getSurfaceItems,
} from 'owa-calendar-surface';
import {
    createItemsDecorator,
    createMonthItemsDecorator,
    CalendarSurfaceStore,
    LinearItemsLayout,
    DatesColumnProvider,
    inDayItemTemplate,
    allDayItemTemplate,
    isOverflowIndicatorId,
    CalendarSurfaceTransformProps,
} from 'owa-calendar-surface-dategrid';

export function createPrintViewConfig<TEvent>(
    store: CalendarSurfaceStore,
    colorProvider: (model: TEvent) => ColorScheme,
    getCharm: (model: TEvent) => number
): CalendarSurfaceConfig {
    let itemsDecorator = createItemsDecorator(store, () => false);
    const inDayItemConfig = {
        itemsEventHandlers: null,
        itemsDecorator: itemsDecorator,
        template: (store, model, state) =>
            inDayItemTemplate(
                store,
                model,
                colorProvider(model),
                getCharm(model),
                state,
                true /* increase font size */,
                false /* join online button*/
            ),
        colorProvider: colorProvider,
    };
    const allDayItemConfig = {
        itemsEventHandlers: null,
        itemsDecorator: itemsDecorator,
        template: (store, model, state) =>
            allDayItemTemplate(
                store,
                model,
                colorProvider(model),
                getCharm(model),
                state,
                true /* increase font size */
            ),
        colorProvider: colorProvider,
    };

    let getItemConfig = (model: CalendarEvent) => {
        if (isOverflowIndicatorId(model.ItemId.Id)) {
            return null;
        }

        if (renderAsAllDay(model)) {
            return allDayItemConfig;
        }

        return inDayItemConfig;
    };

    let dayConfig: CalendarSurfaceTransformProps = {
        columnProvider: new DatesColumnProvider(store),
        itemsLayout: new LinearItemsLayout(store),
        disableWorkingHours: true,
        disableColumnSelection: true,
        getItemConfig: getItemConfig,
        supportExpandableAllDayWell: () => false,
    };

    let monthItemsDecorator = createMonthItemsDecorator(store);
    const monthItemsConfig = {
        itemsEventHandlers: null,
        itemsDecorator: monthItemsDecorator,
        template: (store, model, state) =>
            monthItemTemplate(
                store,
                model,
                colorProvider(model),
                getCharm(model),
                state,
                true /* increase font size */
            ),
        colorProvider: colorProvider,
    };

    const monthConfig = {
        monthDayCellsConfig: createMonthDayCellConfig(store),
        getItemConfig: event => (!isOverflowIndicatorId(event.ItemId.Id) ? monthItemsConfig : null),
    };

    return {
        dayViewConfig: {
            calendarDayContentConfigProvider: () => {
                return [
                    {
                        content: createCalendarDayContent(
                            store,
                            dayConfig,
                            () => {
                                return getSurfaceItems(store);
                            },
                            undefined /* calendarFolderId */,
                            undefined /* customEventsComparator */,
                            undefined /* disableDateHeader */,
                            undefined /* customTimeMarkerClassName */,
                            undefined /* calendarWorkingHours */,
                            true /* hideTodayEmphasis */
                        ),
                    },
                ];
            },
            setFocusedElement: () => {},
        },
        monthViewConfig: {
            contentGenerator: () =>
                getCalendarMonthContent(
                    store,
                    monthConfig,
                    undefined /* searchText */,
                    true /* hideTodayEmphasis */
                ),
            setFocusedElement: () => {},
        },
    };
}

function createMonthDayCellConfig(store: CalendarSurfaceStore) {
    return {
        itemsEventHandlers: null,
        itemsDecorator: createMonthDayCellsDecorator(store),
    };
}
